import { useEffect, useRef, useState } from "react";

import { HealthRing } from "@/components/HealthRing";
import { t } from "@/i18n";
import { fmtInt } from "@/lib/utils";

export interface HealthBreakdownItem {
  kpi_id: string;
  label: string;
  weight: number;
  kpi_score: number | null;
  kpi_count: number;
  kpi_critical: number;
  kpi_warning: number;
  contribution: number;
  lost_points: number;
}

interface HealthRingPopoverProps {
  value: number | null;
  breakdown: HealthBreakdownItem[];
  /** Halkanin yaninda hukum basligi/aciklamasi (varsayilan acik). */
  showVerdict?: boolean;
  size?: number;
}

/** Skora gore sozel hukum + aciklama (4 bant + bilinmeyen). */
export function healthVerdict(value: number | null): { title: string; desc: string } {
  if (value == null) {
    return { title: t("health.verdict.bilinmiyor_baslik"), desc: t("health.verdict.bilinmiyor_aciklama") };
  }
  if (value < 50) return { title: t("health.verdict.acil_baslik"), desc: t("health.verdict.acil_aciklama") };
  if (value < 70) return { title: t("health.verdict.risk_baslik"), desc: t("health.verdict.risk_aciklama") };
  if (value < 90) return { title: t("health.verdict.dikkat_baslik"), desc: t("health.verdict.dikkat_aciklama") };
  return { title: t("health.verdict.temiz_baslik"), desc: t("health.verdict.temiz_aciklama") };
}

/**
 * Saglik halkasi + hukum + "neden bu skor?" dokum popover'i (eski
 * `#healthCard` + `#breakdownPopover`). Halka ya da baslik tiklaninca
 * dokum acilir; disina tiklayinca kapanir.
 */
export function HealthRingPopover({ value, breakdown, showVerdict = true, size = 96 }: HealthRingPopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const verdict = healthVerdict(value);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex items-center gap-3 sm:gap-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={t("health.breakdown.toggle")}
        className="shrink-0 rounded-full"
      >
        <HealthRing value={value} size={size} />
      </button>
      {showVerdict && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="min-w-0 text-left" data-testid="health-verdict">
          <p className="font-display text-base font-bold leading-tight text-ink sm:text-lg">{verdict.title}</p>
          <p className="mt-0.5 text-[13px] leading-snug text-muted">{verdict.desc}</p>
          <p className="mt-1 text-[11px] text-muted">ⓘ {t("health.breakdown.ipucu")}</p>
        </button>
      )}
      {open && (
        <div
          role="dialog"
          aria-label={t("health.breakdown.title")}
          className="absolute left-0 top-full z-30 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-card border-[1.5px] border-line bg-card p-4 shadow-modal"
        >
          <h4 className="mb-1 font-display text-sm font-bold text-ink">{t("health.breakdown.title")}</h4>
          <p className="mb-3 text-xs text-muted">{t("health.breakdown.sub")}</p>
          <ul className="space-y-1">
            {breakdown.map((row) => {
              const lost = row.lost_points;
              const cls = lost <= 0 ? "text-muted" : lost < row.weight ? "text-amber" : "text-red";
              return (
                <li key={row.kpi_id} className="grid grid-cols-[1fr_auto] items-center gap-2 border-b border-bg py-1.5 text-xs last:border-b-0">
                  <span>
                    <span className="font-medium text-ink">{row.label}</span>
                    <span className="block text-[11px] text-muted">
                      {t("health.breakdown.agirlik", { n: String(row.weight) })}
                      {row.kpi_critical > 0 && ` · ${fmtInt(row.kpi_critical)} ${t("ozet.kargo_sirketi.kritik")}`}
                      {row.kpi_warning > 0 && ` · ${fmtInt(row.kpi_warning)} ${t("ozet.kargo_sirketi.uyari")}`}
                    </span>
                  </span>
                  <span className={`num font-semibold ${cls}`}>{lost > 0 ? `−${lost.toFixed(1)}` : "✓"}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
